import { useState, useCallback, useRef, useEffect } from 'react';
import type { Transformable, SelectionBox } from './useTransformable';

interface UseSelectionToolProps {
  elements: Transformable[];
  selected: Set<string>;
  setSelected: (ids: Set<string>) => void;
}

export const useSelectionTool = ({
  elements,
  selected,
  setSelected,
}: UseSelectionToolProps) => {
  const [selecting, setSelecting] = useState(false);
  const [selection, setSelection] = useState<SelectionBox | null>(null);
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const getCanvasCoordinates = useCallback((event: React.MouseEvent | MouseEvent) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  const isPointInRect = useCallback((x: number, y: number, rectX: number, rectY: number, rectWidth: number, rectHeight: number) => {
    return x >= rectX && x <= rectX + rectWidth && y >= rectY && y <= rectY + rectHeight;
  }, []);

  const handleCanvasMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    
    const target = event.target as HTMLElement;
    
    // Don't start selection if clicking on elements or handles
    if (target.classList.contains('element') || 
        target.classList.contains('resize-handle') || 
        target.classList.contains('rotate-handle')) {
      return;
    }
    
    const coords = getCanvasCoordinates(event);
    
    // Clear selection if not holding shift
    if (!event.shiftKey) {
      setSelected(new Set());
    }
    
    // Start selection box
    setSelecting(true);
    selectionStartRef.current = coords;
    setSelection({ x: coords.x, y: coords.y, width: 0, height: 0 });
  }, [getCanvasCoordinates, setSelected]);

  const handleCanvasMouseMove = useCallback((event: MouseEvent) => {
    if (!selecting || !selectionStartRef.current) return;
    
    const coords = getCanvasCoordinates(event);
    const start = selectionStartRef.current;
    
    // Update selection box
    const newSelection = {
      x: Math.min(start.x, coords.x),
      y: Math.min(start.y, coords.y),
      width: Math.abs(coords.x - start.x),
      height: Math.abs(coords.y - start.y),
    };
    
    setSelection(newSelection);
  }, [selecting, getCanvasCoordinates]);

  const handleCanvasMouseUp = useCallback(() => {
    if (!selecting || !selection) return;
    
    // Find elements that intersect with the selection box
    const selectedElements = elements.filter(element => {
      const centerX = element.x + element.origin[0] * element.width;
      const centerY = element.y + element.origin[1] * element.height;
      return isPointInRect(centerX, centerY, selection.x, selection.y, selection.width, selection.height);
    });
    
    if (selectedElements.length > 0) {
      const newSelection = new Set(selected);
      selectedElements.forEach(element => newSelection.add(element.id));
      setSelected(newSelection);
    }
    
    // Reset selection state
    setSelecting(false);
    setSelection(null);
    selectionStartRef.current = null;
  }, [selecting, selection, elements, selected, setSelected, isPointInRect]);

  const setCanvasRef = useCallback((ref: HTMLDivElement | null) => {
    canvasRef.current = ref;
  }, []);

  // Handle global mouse event listeners internally
  useEffect(() => {
    document.addEventListener('mousemove', handleCanvasMouseMove);
    document.addEventListener('mouseup', handleCanvasMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleCanvasMouseMove);
      document.removeEventListener('mouseup', handleCanvasMouseUp);
    };
  }, [handleCanvasMouseMove, handleCanvasMouseUp]);

  return {
    selecting,
    selection,
    setCanvasRef,
    handleCanvasMouseDown,
  };
};
